/* ==========================================================================
   WC26 GAME - "Play & Win" World Cup 2026 game section
   Vanilla JS. No dependencies. Multi-instance safe. Prefix: wc26g
   --------------------------------------------------------------------------
   Backend is reached ONLY through the Shopify App Proxy (config.proxyPath).
   Never call https://wc26-game.mikesport.tech directly.
   ========================================================================== */
(function () {
  "use strict";

  var ENDPOINTS = {
    sessionStart: "/api/session/start", // POST {game} -> {sessionId}
    gameFinish:   "/api/game/finish",   // POST {game, score, sessionId, duration} -> result
    playerSubmit: "/api/player/submit", // POST {sessionId, game, name, phone} -> {ok}
    leaderboard:  "/api/leaderboard",   // GET  ?game=&limit= -> {entries:[{name,score,phone?}]}
    rewardRules:  "/api/reward-rules"   // GET  -> {tiers:[{score,label}]}
  };

  var GAME_KEYS = ["flappy_ball", "goalkeeper"];
  var registry = {};
  var activePlaying = null;
  var keyHandlerBound = false;

  /* ----------------------------- utils ----------------------------------- */
  function $(root, sel) { return root.querySelector(sel); }
  function $all(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }
  function fmtClock(sec) {
    sec = Math.max(0, Math.floor(sec));
    var m = Math.floor(sec / 60), s = sec % 60;
    return m + ":" + (s < 10 ? "0" : "") + s;
  }
  function pad2(n) { return (n < 10 ? "0" : "") + n; }
  function tpl(str, map) {
    return String(str || "").replace(/\{(\w+)\}/g, function (_, k) { return map[k] != null ? map[k] : ""; });
  }
  function fmtNum(n) { try { return Number(n).toLocaleString("en-US"); } catch (e) { return String(n); } }
  function firstDefined() {
    for (var i = 0; i < arguments.length; i++) {
      if (arguments[i] !== undefined && arguments[i] !== null && arguments[i] !== "") return arguments[i];
    }
    return undefined;
  }
  function loadImg(src) { if (!src) return null; var im = new Image(); im.decoding = "async"; im.src = src; return im; }
  function imgReady(im) { return im && im.complete && im.naturalWidth > 0; }

  /* ============================ API helper =============================== */
  function Api(proxyPath) { this.base = (proxyPath || "/apps/world-cup-game").replace(/\/+$/, ""); }
  Api.prototype.url = function (ep) { return this.base + ep; };
  Api.prototype.request = function (ep, opts) {
    opts = opts || {};
    var cfg = { method: opts.method || "GET", credentials: "same-origin", headers: { "Accept": "application/json" } };
    if (opts.body !== undefined) {
      cfg.method = opts.method || "POST";
      cfg.headers["Content-Type"] = "application/json";
      cfg.body = JSON.stringify(opts.body);
    }
    var ctrl;
    if (typeof AbortController !== "undefined") {
      ctrl = new AbortController(); cfg.signal = ctrl.signal;
      setTimeout(function () { try { ctrl.abort(); } catch (e) {} }, opts.timeout || 12000);
    }
    return fetch(this.url(ep), cfg).then(function (res) {
      return res.text().then(function (txt) {
        var data = null;
        if (txt) { try { data = JSON.parse(txt); } catch (e) { data = null; } }
        if (!res.ok) { var err = new Error((data && (data.message || data.error)) || ("HTTP " + res.status)); err.status = res.status; err.data = data; throw err; }
        return data || {};
      });
    });
  };
  Api.prototype.get = function (ep, params) {
    var q = "";
    if (params) { var parts = []; for (var k in params) if (params.hasOwnProperty(k) && params[k] != null) parts.push(encodeURIComponent(k) + "=" + encodeURIComponent(params[k])); if (parts.length) q = "?" + parts.join("&"); }
    return this.request(ep + q, { method: "GET" });
  };
  Api.prototype.post = function (ep, body) { return this.request(ep, { method: "POST", body: body || {} }); };

  /* ========================================================================
     INSTANCE
     ===================================================================== */
  function Instance(root) {
    this.root = root;
    this.id = root.getAttribute("data-section-id") || ("wc26g-" + Math.floor(Math.random() * 1e6));
    this.cfg = this._readConfig();
    this.api = new Api(this.cfg.proxyPath);
    this.mult = Number(this.cfg.scoreMultiplier) > 0 ? Number(this.cfg.scoreMultiplier) : 1;

    this.cleanups = [];
    this.timers = [];
    this.currentGame = null;
    this.session = null;
    this.lastResult = null;
    this.claimedSessions = {};
    this.rewardRules = null;
    this.engine = null;
    this.bestByGame = { flappy_ball: 0, goalkeeper: 0 };

    this.mqDesktop = window.matchMedia("(min-width: 768px)");

    this._cacheDom();
    this._preloadImages();
    this._bindHome();
    this._bindAccordions();
    this._applyBackgrounds();
    this._bindMedia();
    this._initLeaderboards();
    this._fetchRewardRules();
    this._restoreStickyReward();
  }

  Instance.prototype._readConfig = function () {
    var node = this.root.querySelector("[data-wc26g-config]");
    var cfg = {};
    if (node) { try { cfg = JSON.parse(node.textContent); } catch (e) { cfg = {}; } }
    cfg.proxyPath = cfg.proxyPath || "/apps/world-cup-game";
    cfg.labels = cfg.labels || {};
    cfg.images = cfg.images || {};
    cfg.games = cfg.games || {};
    cfg.leaderboard = cfg.leaderboard || {};
    cfg.obstaclePairs = cfg.obstaclePairs || [];
    cfg.obstaclesLegacy = cfg.obstaclesLegacy || [];
    return cfg;
  };

  Instance.prototype._cacheDom = function () {
    var r = this.root;
    this.dom = {
      bg: $(r, "[data-wc26g-bg]"),
      tint: $(r, "[data-wc26g-tint]"),
      fog: $(r, "[data-wc26g-fog]"),
      boardBg: $(r, "[data-wc26g-board-bg]"),
      screens: { home: $(r, '[data-wc26g-screen="home"]'), game: $(r, '[data-wc26g-screen="game"]') },
      gameTitle: $(r, "[data-wc26g-game-title]"),
      hud: $(r, "[data-wc26g-hud]"),
      board: $(r, "[data-wc26g-board]"),
      canvas: $(r, "[data-wc26g-canvas]"),
      gk: $(r, "[data-wc26g-gk]"),
      gkBalls: $(r, "[data-wc26g-gk-balls]"),
      gkKeeper: $(r, "[data-wc26g-gk-keeper]"),
      gkVisual: $(r, "[data-wc26g-gk-visual]"),
      gkHint: $(r, "[data-wc26g-gk-hint]"),
      gkGoal: $(r, "[data-wc26g-gk-goal]"),
      gkLine: $(r, "[data-wc26g-gk-line]"),
      flash: $(r, "[data-wc26g-flash]"),
      overlays: {
        start: $(r, '[data-wc26g-overlay="start"]'),
        regs: $(r, '[data-wc26g-overlay="regs"]'),
        countdown: $(r, '[data-wc26g-overlay="countdown"]'),
        result: $(r, '[data-wc26g-overlay="result"]'),
        loading: $(r, '[data-wc26g-overlay="loading"]')
      },
      startHint: $(r, "[data-wc26g-start-hint]"),
      regsTitle: $(r, "[data-wc26g-regs-title]"),
      regsBadge: $(r, "[data-wc26g-regs-badge]"),
      regsRules: $(r, "[data-wc26g-regs-rules]"),
      regsBody: $(r, "[data-wc26g-regs-body]"),
      letsGo: $(r, "[data-wc26g-lets-go]"),
      count: $(r, "[data-wc26g-count]"),
      result: $(r, "[data-wc26g-result]"),
      accordions: $all(r, "[data-wc26g-acc]")
    };
  };

  Instance.prototype._preloadImages = function () {
    var imgs = this.cfg.images || {};
    this.assets = {
      ball: loadImg(imgs.mainBall),
      gkBall: loadImg(imgs.gkBall || imgs.mainBall),
      gkCatcher: loadImg(imgs.gkCatcher)
    };

    /* Preload all backgrounds + obstacle images so the board is not black
       during regulations, countdown, or the first seconds of gameplay. */
    this.preloadedImages = [];
    var urls = [];
    function add(u) { if (u && urls.indexOf(u) === -1) urls.push(u); }
    [imgs.sectionBgDesktop, imgs.sectionBgMobile, imgs.homeBgDesktop, imgs.homeBgMobile, imgs.gameBgDesktop, imgs.gameBgMobile, imgs.fog, imgs.mainBall, imgs.gkBall, imgs.gkCatcher].forEach(add);
    GAME_KEYS.forEach(function (key) {
      var g = (this.cfg.games || {})[key] || {};
      add(g.bgDesktop); add(g.bgMobile);
    }, this);
    (this.cfg.obstaclePairs || []).forEach(function (p) { add(p.topD); add(p.botD); add(p.topM); add(p.botM); });
    (this.cfg.obstaclesLegacy || []).forEach(add);
    for (var i = 0; i < urls.length; i++) this.preloadedImages.push(loadImg(urls[i]));
  };

  /* ------------------------- backgrounds -------------------------------- */
  Instance.prototype._bindMedia = function () {
    var self = this;
    this._mqHandler = function () {
      self._applyBackgrounds();
      if (self.engine && self.engine.onViewportChange) self.engine.onViewportChange();
    };
    if (this.mqDesktop.addEventListener) this.mqDesktop.addEventListener("change", this._mqHandler);
    else if (this.mqDesktop.addListener) this.mqDesktop.addListener(this._mqHandler);
    this.cleanups.push(function () {
      if (self.mqDesktop.removeEventListener) self.mqDesktop.removeEventListener("change", self._mqHandler);
      else if (self.mqDesktop.removeListener) self.mqDesktop.removeListener(self._mqHandler);
    });
  };

  Instance.prototype.isDesktop = function () { return this.mqDesktop.matches; };

  Instance.prototype._applyBackgrounds = function () {
    var imgs = this.cfg.images || {};
    var isD = this.isDesktop();

    var sectionUrl = isD
      ? firstDefined(imgs.sectionBgDesktop, imgs.sectionBgMobile, imgs.homeBgDesktop, imgs.homeBgMobile)
      : firstDefined(imgs.sectionBgMobile, imgs.sectionBgDesktop, imgs.homeBgMobile, imgs.homeBgDesktop);

    var homeUrl = isD
      ? firstDefined(imgs.homeBgDesktop, imgs.sectionBgDesktop, imgs.homeBgMobile, imgs.sectionBgMobile)
      : firstDefined(imgs.homeBgMobile, imgs.sectionBgMobile, imgs.homeBgDesktop, imgs.sectionBgDesktop);

    this.sectionBgUrl = sectionUrl || "";
    this.homeBgUrl = homeUrl || "";

    if (this.root) {
      this.root.style.backgroundImage = sectionUrl ? ('url("' + sectionUrl + '")') : "";
      this.root.style.backgroundSize = sectionUrl ? "cover" : "";
      this.root.style.backgroundPosition = sectionUrl ? "center" : "";
      this.root.style.backgroundRepeat = sectionUrl ? "no-repeat" : "";
    }

    if (this.dom.bg) {
      this.dom.bg.style.backgroundImage = homeUrl ? ('url("' + homeUrl + '")') : "";
      this.dom.bg.style.backgroundSize = homeUrl ? "cover" : "";
      this.dom.bg.style.backgroundPosition = homeUrl ? "center" : "";
      this.dom.bg.style.backgroundRepeat = homeUrl ? "no-repeat" : "";
    }
    if (this.dom.fog && imgs.fog) this.dom.fog.style.backgroundImage = 'url("' + imgs.fog + '")';
    if (this.currentGame) this._applyBoardBackground();
  };

  Instance.prototype._applyBoardBackground = function () {
    var imgs = this.cfg.images || {};
    var g = (this.currentGame && this.cfg.games[this.currentGame]) || {};
    var isD = this.isDesktop();

    /* Priority: selected game bg -> generic game bg -> section bg -> home bg.
       Apply the same URL to all play layers so regulations, countdown, gameplay,
       and result states never fall back to the black board. */
    var url = isD
      ? firstDefined(g.bgDesktop, imgs.gameBgDesktop, imgs.sectionBgDesktop, imgs.homeBgDesktop, g.bgMobile, imgs.gameBgMobile, imgs.sectionBgMobile, imgs.homeBgMobile)
      : firstDefined(g.bgMobile, imgs.gameBgMobile, imgs.sectionBgMobile, imgs.homeBgMobile, g.bgDesktop, imgs.gameBgDesktop, imgs.sectionBgDesktop, imgs.homeBgDesktop);

    this.boardBgUrl = url || "";
    var bgVal = url ? ('url("' + url + '")') : "";
    [this.dom.boardBg, this.dom.board, this.dom.gk, this.dom.canvas].forEach(function (el) {
      if (!el) return;
      el.style.backgroundImage = bgVal;
      el.style.backgroundSize = url ? "cover" : "";
      el.style.backgroundPosition = url ? "center" : "";
      el.style.backgroundRepeat = url ? "no-repeat" : "";
    });
  };

  /* ----------------------------- screens -------------------------------- */
  Instance.prototype._showScreen = function (name) {
    var s = this.dom.screens;
    Object.keys(s).forEach(function (k) { if (s[k]) { var on = (k === name); s[k].hidden = !on; s[k].classList.toggle("is-active", on); } });
    this.root.classList.toggle("wc26g--ingame", name === "game");
    this._applyBackgrounds();
  };

  Instance.prototype._scrollGameIntoView = function (instant) {
    if (this.isDesktop()) return;
    var target = this.dom.gameTitle || this.dom.board || this.root;
    if (!target || !target.scrollIntoView) return;
    var behavior = instant ? "auto" : (this.cfg.enableAnimations ? "smooth" : "auto");
    this.timers.push(setTimeout(function () {
      try { target.scrollIntoView({ behavior: behavior, block: "start", inline: "nearest" }); } catch (e) { target.scrollIntoView(true); }
    }, instant ? 0 : 60));
  };

  /* ------------------------------ home ---------------------------------- */
  Instance.prototype._bindHome = function () {
    var self = this;
    $all(this.root, "[data-wc26g-pick]").forEach(function (btn) {
      var h = function () { self.pickGame(btn.getAttribute("data-wc26g-pick")); };
      btn.addEventListener("click", h);
      self.cleanups.push(function () { btn.removeEventListener("click", h); });
    });
  };

  /* -------------------------- accordions / LB --------------------------- */
  Instance.prototype._bindAccordions = function () {
    var self = this;
    this.dom.accordions.forEach(function (acc) {
      var head = $(acc, "[data-wc26g-acc-head]");
      var body = $(acc, "[data-wc26g-acc-body]");
      if (!head || !body) return;
      var h = function () { self._toggleAcc(acc, !acc.classList.contains("is-open")); };
      head.addEventListener("click", h);
      self.cleanups.push(function () { head.removeEventListener("click", h); });
    });
  };
  Instance.prototype._toggleAcc = function (acc, open) {
    var body = $(acc, "[data-wc26g-acc-body]");
    var head = $(acc, "[data-wc26g-acc-head]");
    acc.classList.toggle("is-open", !!open);
    if (body) body.hidden = !open;
    if (head) head.setAttribute("aria-expanded", open ? "true" : "false");
  };

  Instance.prototype._initLeaderboards = function () {
    if (!this.cfg.leaderboard.enabled) return;
    var self = this;
    this.dom.accordions.forEach(function (acc) { self.refreshLeaderboard(acc.getAttribute("data-wc26g-acc")); });
    var def = this.cfg.leaderboard.defaultOpen || "flappy_ball";
    this.dom.accordions.forEach(function (acc) {
      var key = acc.getAttribute("data-wc26g-acc");
      var open = (def === "both") || (def === key);
      self._toggleAcc(acc, open);
    });
  };

  Instance.prototype._accFor = function (gameKey) {
    for (var i = 0; i < this.dom.accordions.length; i++) if (this.dom.accordions[i].getAttribute("data-wc26g-acc") === gameKey) return this.dom.accordions[i];
    return null;
  };

  Instance.prototype.refreshLeaderboard = function (gameKey) {
    if (!this.cfg.leaderboard.enabled) return;
    var acc = this._accFor(gameKey);
    var self = this;
    var limit = this.cfg.leaderboard.count || 5;
    var body = acc ? $(acc, "[data-wc26g-acc-body]") : null;
    if (body) body.innerHTML = '<div class="wc26g-lb-loading">Loading...</div>';
    this.api.get(ENDPOINTS.leaderboard, { game: gameKey, limit: limit }).then(function (data) {
      var entries = self._normalizeLeaderboard(data);
      self.bestByGame[gameKey] = entries.length ? entries[0].score : 0;
      if (self.currentGame === gameKey && self.hudRefs && self.hudRefs.best) self.hudRefs.best.textContent = fmtNum(self._best(gameKey));
      if (body) self._renderLeaderboard(body, entries);
    }).catch(function () {
      if (body) body.innerHTML = '<div class="wc26g-lb-empty">' + esc(self.cfg.labels.leaderboardEmpty || "No scores yet.") + "</div>";
    });
  };

  Instance.prototype._normalizeLeaderboard = function (data) {
    var arr = [];
    if (Array.isArray(data)) arr = data;
    else if (data && Array.isArray(data.entries)) arr = data.entries;
    else if (data && Array.isArray(data.leaderboard)) arr = data.leaderboard;
    else if (data && Array.isArray(data.players)) arr = data.players;
    return arr.map(function (e) {
      return {
        name: firstDefined(e.name, e.username, e.player, "Anonymous"),
        score: Number(firstDefined(e.score, e.points, e.value, 0)) || 0,
        phone: firstDefined(e.phone, e.phone_number, e.phoneNumber, "") || ""
      };
    });
  };

  Instance.prototype._renderLeaderboard = function (body, entries) {
    var limit = this.cfg.leaderboard.count || 5;
    entries = entries.slice(0, limit);
    if (!entries.length) { body.innerHTML = '<div class="wc26g-lb-empty">' + esc(this.cfg.labels.leaderboardEmpty || "No scores yet.") + "</div>"; return; }
    var showPhone = !!this.cfg.leaderboard.showPhones;
    body.innerHTML = entries.map(function (e, i) {
      var rank = i + 1;
      var rc = rank <= 3 ? (" wc26g-lb-rank--" + rank) : "";
      var medal = rank === 1 ? "&#129351;" : rank === 2 ? "&#129352;" : rank === 3 ? "&#129353;" : rank;
      var phone = (showPhone && e.phone) ? '<span class="wc26g-lb-phone">' + esc(e.phone) + "</span>" : "";
      return '<div class="wc26g-lb-row"><span class="wc26g-lb-rank' + rc + '">' + medal + '</span><span class="wc26g-lb-name">' + esc(e.name) + phone + '</span><span class="wc26g-lb-score">' + fmtNum(e.score) + "</span></div>";
    }).join("");
  };

  /* --------------------------- reward rules ----------------------------- */
  Instance.prototype._fetchRewardRules = function () {
    var self = this;
    this.api.get(ENDPOINTS.rewardRules).then(function (data) {
      var tiers = [];
      if (Array.isArray(data)) tiers = data;
      else if (data && Array.isArray(data.tiers)) tiers = data.tiers;
      else if (data && Array.isArray(data.rewards)) tiers = data.rewards;
      self.rewardRules = tiers.map(function (t) {
        return { score: Number(firstDefined(t.score, t.threshold, t.points, 0)) || 0, label: firstDefined(t.label, t.name, t.reward, "") };
      }).filter(function (t) { return t.score > 0; }).sort(function (a, b) { return a.score - b.score; });
    }).catch(function () { self.rewardRules = null; });
  };
  Instance.prototype._nextReward = function (score) {
    if (this.rewardRules && this.rewardRules.length) for (var i = 0; i < this.rewardRules.length; i++) if (this.rewardRules[i].score > score) return this.rewardRules[i];
    return null;
  };

  /* ============================ GAME FLOW =============================== */
  Instance.prototype.pickGame = function (gameKey) {
    if (GAME_KEYS.indexOf(gameKey) === -1) return;
    var g = this.cfg.games[gameKey];
    if (!g || !g.enabled) return;
    this.currentGame = gameKey;
    this.dom.gameTitle.textContent = g.title || gameKey;
    this._applyBoardBackground();
    this._buildHud(gameKey);
    this._showScreen("game");
    this._showRegulations();
    this._scrollGameIntoView(false);
  };

  Instance.prototype._best = function (gameKey) {
    var lb = this.bestByGame[gameKey] || 0;
    var local = this.getBest(gameKey);
    return Math.max(lb, local);
  };

  Instance.prototype._buildHud = function (gameKey) {
    var L = this.cfg.labels;
    var best = this._best(gameKey);
    var time = ["time", L.timeLabel || "TIME", "0:00"];
    var score = ["score", L.scoreLabel || "SCORE", "0"];
    var bestC = ["best", L.bestLabel || "BEST", fmtNum(best)];
    var order = gameKey === "flappy_ball" ? [time, score, bestC] : [score, time, bestC];
    this.dom.hud.innerHTML = order.map(function (c) {
      return '<div class="wc26g-hud__cell wc26g-hud__cell--' + c[0] + '" data-wc26g-hud="' + c[0] + '"><span class="wc26g-hud__num" data-wc26g-hud-num>' + c[2] + '</span><span class="wc26g-hud__label">' + c[1] + "</span></div>";
    }).join("");
    this.hudRefs = { time: this._hudNum("time"), score: this._hudNum("score"), best: this._hudNum("best") };
  };
  Instance.prototype._hudNum = function (key) {
    var cell = this.dom.hud.querySelector('[data-wc26g-hud="' + key + '"]');
    return cell ? cell.querySelector("[data-wc26g-hud-num]") : null;
  };

  /* --------------------------- regulations ------------------------------ */
  Instance.prototype._rulesBlock = function () {
    var map = this.cfg.rulesBlocks || {};
    var list = map[this.currentGame] || [];
    return list.length ? list[0] : null;
  };

  Instance.prototype._showRegulations = function () {
    var g = this.cfg.games[this.currentGame] || {};
    var imgs = this.cfg.images || {};
    var block = this._rulesBlock();
    var iconUrl = block && block.headerIcon ? block.headerIcon : (this.currentGame === "goalkeeper"
      ? firstDefined(imgs.goalkeeperRulesIcon, imgs.gkBall, imgs.mainBall)
      : firstDefined(imgs.flappyRulesIcon, imgs.mainBall));

    this.dom.regsTitle.textContent = (block && block.title) || g.regsTitle || "Regulations";
    if (this.dom.regsBadge) {
      if (iconUrl) this.dom.regsBadge.innerHTML = '<img src="' + esc(iconUrl) + '" alt="" loading="lazy">';
      else this.dom.regsBadge.innerHTML = this.currentGame === "goalkeeper" ? "&#129508;" : "&#9917;";
    }

    if (this.dom.regsRules) { this.dom.regsRules.hidden = true; this.dom.regsRules.innerHTML = ""; }
    this.dom.regsBody.innerHTML = this._regulationsMarkup(g.regs || "", iconUrl, block);
    this.dom.letsGo.textContent = (block && block.buttonLabel) || this.cfg.labels.letsGo || "Let's GO!";
    this._hideAllOverlays();
    this._showOverlay("regs");
    var self = this;
    if (this._letsGoHandler) this.dom.letsGo.removeEventListener("click", this._letsGoHandler);
    this._letsGoHandler = function () { self._startCountdown(); };
    this.dom.letsGo.addEventListener("click", this._letsGoHandler);
  };

  Instance.prototype._splitRegsText = function (text) {
    var lines = String(text || "").split(/\n+/).map(function (x) { return x.trim(); }).filter(Boolean);
    var out = {
      headline: "Play. Score. Unlock Rewards.",
      intro: "",
      question: "Think you can beat the score?",
      tiersTitle: "Rewards Tiers:",
      noSign: "No signup required!",
      notes: []
    };
    if (lines.length) out.headline = lines.shift();
    lines.forEach(function (line) {
      if (/^Rewards\s*Tiers:?$/i.test(line)) { out.tiersTitle = line.replace(/:?$/, ":"); return; }
      if (/^\s*\d[\d,\.]*\s*pts\s*=/i.test(line)) return;
      if (/Think you can beat/i.test(line)) { out.question = line; return; }
      if (/No\s*sign|No signup/i.test(line)) { out.noSign = line.replace(/\.$/, "!"); return; }
      if (!out.intro && (line.indexOf(":") !== -1 || /Flappy|Goalkeeper/i.test(line))) { out.intro = line; return; }
      if (/Reward Code|valid|one time|Mike Sport|reserves/i.test(line)) out.notes.push(line);
    });
    if (!out.intro) {
      out.intro = this.currentGame === "goalkeeper"
        ? "Goalkeeper: Slide to block the goal. Survive 60 seconds. Unlock your rewards!"
        : "Flappy Ball: Tap the screen to fly. Pass through the gaps. Don't get hit!";
    }
    if (!out.notes.length) out.notes = ["Reward Code valid for 2 hours after unlocking. One time use only.", "Mike Sport reserves the right to modify or end rewards at any time."];
    return out;
  };

  Instance.prototype._regsData = function (text, block) {
    var data = this._splitRegsText(text);
    if (!block) return data;
    return {
      headline: block.headline || data.headline,
      intro: block.intro || data.intro,
      question: block.question || data.question,
      tiersTitle: block.tiersTitle || data.tiersTitle || "Rewards Tiers:",
      noSign: block.noSign || data.noSign,
      notes: [block.note1, block.note2].filter(Boolean).length ? [block.note1, block.note2].filter(Boolean) : data.notes
    };
  };

  Instance.prototype._regsTiers = function (block) {
    var custom = block && block.tiers ? block.tiers.filter(function (t) { return t && (t.score || t.label || t.icon); }) : [];
    var tiers = custom.length ? custom : ((this.rewardRules && this.rewardRules.length) ? this.rewardRules.slice(0, 3) : [
      { score: 1000, label: "Free Shipping" },
      { score: 1500, label: "15% off World Cup Collection" },
      { score: 2000, label: "10% off on Everything!" }
    ]);
    while (tiers.length < 3) tiers.push({ score: "", label: "Reward" });
    return tiers.slice(0, 3);
  };

  Instance.prototype._regTierIcon = function (idx, tier) {
    var imgs = this.cfg.images || {};
    var keys = ["rewardTier1Icon", "rewardTier2Icon", "rewardTier3Icon"];
    var fallback = ["&#128666;", "&#127941;", "&#127942;"];
    var url = tier && tier.icon ? tier.icon : imgs[keys[idx]];
    return url ? '<img src="' + esc(url) + '" alt="" loading="lazy">' : '<span aria-hidden="true">' + fallback[idx] + '</span>';
  };

  Instance.prototype._tierScoreText = function (score) {
    if (score === undefined || score === null || score === "") return "&nbsp;";
    var str = String(score).trim();
    if (/pts/i.test(str)) return esc(str);
    return esc(fmtNum(str) + " pts");
  };

  Instance.prototype._regulationsMarkup = function (text, iconUrl, block) {
    var data = this._regsData(text, block);
    var gameIcon = iconUrl ? '<img src="' + esc(iconUrl) + '" alt="" loading="lazy">' : (this.currentGame === "goalkeeper" ? "&#129508;" : "&#9917;");
    var tiers = this._regsTiers(block);
    var html = '';
    html += '<div class="wc26g-regs-modern">';
    html += '<h3 class="wc26g-regs-modern__headline">' + esc(data.headline) + '</h3>';
    html += '<p class="wc26g-regs-modern__intro"><span class="wc26g-regs-modern__inline-icon">' + gameIcon + '</span><span>' + esc(data.intro) + '</span></p>';
    html += '<p class="wc26g-regs-modern__question">' + esc(data.question) + '</p>';
    html += '<h4 class="wc26g-regs-modern__tiers-title">' + esc(data.tiersTitle || "Rewards Tiers:") + '</h4>';
    html += '<div class="wc26g-regs-modern__tiers">';
    for (var i = 0; i < tiers.length; i++) {
      html += '<div class="wc26g-regs-modern__tier">';
      html += '<div class="wc26g-regs-modern__score">' + this._tierScoreText(tiers[i].score) + '</div>';
      html += '<div class="wc26g-regs-modern__icon">' + this._regTierIcon(i, tiers[i]) + '</div>';
      html += '<div class="wc26g-regs-modern__label">' + esc(tiers[i].label || "Reward") + '</div>';
      html += '</div>';
    }
    html += '</div>';
    html += '<h4 class="wc26g-regs-modern__nosign">' + esc(data.noSign) + '</h4>';
    html += '<div class="wc26g-regs-modern__notes">' + data.notes.map(function (n) { return '<p>' + esc(n) + '</p>'; }).join('') + '</div>';
    html += '</div>';
    return html;
  };

  Instance.prototype._hideAllOverlays = function () { var o = this.dom.overlays; Object.keys(o).forEach(function (k) { if (o[k]) o[k].hidden = true; }); };
  Instance.prototype._showOverlay = function (name) { if (this.dom.overlays[name]) this.dom.overlays[name].hidden = false; };

  /* --------------------------- countdown -------------------------------- */
  Instance.prototype._startCountdown = function () {
    var self = this;
    this._applyBoardBackground();
    this._scrollGameIntoView(true);
    this._hideAllOverlays();
    this._showOverlay("countdown");
    var seq = ["3", "2", "1", "Go!"], i = 0, anim = this.cfg.enableAnimations;
    function step() {
      self.dom.count.textContent = seq[i];
      if (anim) { self.dom.count.style.animation = "none"; void self.dom.count.offsetWidth; self.dom.count.style.animation = ""; }
      i++;
      if (i < seq.length) { self.timers.push(setTimeout(step, 800)); }
      else { self.timers.push(setTimeout(function () { self._beginPlay(); }, 650)); }
    }
    step();
  };

  /* ----------------------------- play ----------------------------------- */
  Instance.prototype._beginPlay = function () {
    var self = this;
    this._applyBoardBackground();
    this._hideAllOverlays();
    this._showOverlay("loading");
    this._startSession().then(function () { self._hideAllOverlays(); self._launchEngine(); })
      .catch(function () {
        self.session = { id: "local-" + Date.now() + "-" + Math.floor(Math.random() * 1e6), source: "local" };
        self._hideAllOverlays(); self._launchEngine();
      });
  };

  Instance.prototype._startSession = function () {
    var self = this;
    return this.api.post(ENDPOINTS.sessionStart, { game: this.currentGame }).then(function (data) {
      var id = firstDefined(data.sessionId, data.session_id, data.id, data.token);
      if (!id) throw new Error("no session id");
      self.session = { id: id, source: "server" };
      return self.session;
    });
  };

  Instance.prototype._launchEngine = function () {
    var self = this;
    this._applyBoardBackground();
    this._destroyEngine();
    activePlaying = this;
    this._lockScroll(true);
    bindKeyHandler();
    var onScore = function (sc) { if (self.hudRefs.score) self.hudRefs.score.textContent = fmtNum(sc); };
    var onTime = function (sec) { if (self.hudRefs.time) self.hudRefs.time.textContent = fmtClock(sec); };
    var onEnd = function (sc, dur) { self._onFinish(sc, dur); };
    if (this.currentGame === "flappy_ball") {
      this.dom.canvas.hidden = false; this.dom.gk.hidden = true;
      this.engine = new FlappyEngine(this, { onScore: onScore, onTime: onTime, onEnd: onEnd });
    } else {
      this.dom.canvas.hidden = true; this.dom.gk.hidden = false;
      this.engine = new GoalkeeperEngine(this, { onScore: onScore, onTime: onTime, onEnd: onEnd });
    }
    this.engine.start();
  };

  Instance.prototype._destroyEngine = function () {
    if (this.engine) { try { this.engine.destroy(); } catch (e) {} this.engine = null; }
    if (activePlaying === this) activePlaying = null;
    this._lockScroll(false);
  };

  /* ----------------------------- finish --------------------------------- */
  Instance.prototype._onFinish = function (displayScore, durationSec) {
    var self = this;
    this._lockScroll(false);
    activePlaying = null;
    displayScore = Math.max(0, Math.round(displayScore));
    this.setBest(this.currentGame, displayScore);
    if (this.hudRefs.best) this.hudRefs.best.textContent = fmtNum(this._best(this.currentGame));

    this.root.classList.add("wc26g--round-ending");
    if (this.dom.board) this.dom.board.classList.add("wc26g-board--round-ending");

    var wait = this.cfg.enableAnimations ? 320 : 0;
    this.timers.push(setTimeout(function () {
      self.root.classList.remove("wc26g--round-ending");
      if (self.dom.board) self.dom.board.classList.remove("wc26g-board--round-ending");
      self._destroyEngine();
      self._applyBoardBackground();
      self.root.classList.add("wc26g--result-pending");

      /* Send the real displayed score to the backend. The old multiplier stays in meta
         for reporting only, because the dashboard rules now use real game points. */
      var payload = {
        game: self.currentGame,
        score: displayScore,
        points: displayScore,
        displayScore: displayScore,
        sessionId: self.session ? self.session.id : null,
        session_id: self.session ? self.session.id : null,
        duration: Math.round(durationSec || 0),
        meta: { displayScore: displayScore, multiplier: self.mult, scoreSent: displayScore, ua: (navigator.userAgent || "").slice(0, 120), ts: new Date().toISOString() }
      };
      self.api.post(ENDPOINTS.gameFinish, payload).then(function (data) {
        var result = self._normalizeResult(data, displayScore);
        self.lastResult = result;
        self.root.classList.remove("wc26g--result-pending");
        self._renderResult(result);
        if (self.cfg.leaderboard.autoRefresh) self.refreshLeaderboard(self.currentGame);
      }).catch(function (err) {
        if (window.console) console.warn("[wc26g] finish failed", err);
        self.root.classList.remove("wc26g--result-pending");
        self._renderResult(self._fallbackResult(displayScore), err);
      });
    }, wait));
  };

  Instance.prototype._normalizeResult = function (data, displayScore) {
    data = data || {};

    /* Important: some backend versions create/confirm the real session id in
       /api/game/finish and return it in the result. If we keep the older
       local/session-start id, /api/player/submit cannot find the score row and
       the leaderboard form fails. Always refresh the session id from the
       authoritative finish response when present. */
    var returnedSessionId = firstDefined(
      data.sessionId,
      data.session_id,
      data.gameSessionId,
      data.game_session_id,
      data.session && (data.session.sessionId || data.session.session_id || data.session.id),
      data.playerScore && (data.playerScore.sessionId || data.playerScore.session_id),
      data.player_score && (data.player_score.sessionId || data.player_score.session_id)
    );
    if (returnedSessionId) this.session = { id: String(returnedSessionId), source: "server" };

    /* Backend can return either nested reward data or top-level fields from
       the dashboard/test flow. Support both so a generated code is never ignored. */
    var reward = data.reward || data.discount || null;
    var selectedRule = data.selectedRule || data.selected_rule || data.rule || data.matchedRule || data.matched_rule || null;
    var topCode = firstDefined(data.discountCode, data.discount_code, data.code, data.promoCode, data.promo_code, data.couponCode, data.coupon_code, null);
    var topLabel = firstDefined(data.rewardLabel, data.reward_label, data.rewardTitle, data.reward_title, data.ruleName, data.rule_name, data.name, selectedRule && (selectedRule.name || selectedRule.label || selectedRule.title), null);
    var topType = firstDefined(data.rewardType, data.reward_type, data.type, selectedRule && (selectedRule.rewardType || selectedRule.reward_type || selectedRule.type), null);
    var topExpires = firstDefined(data.expiresAt, data.expires_at, data.expiry, data.endsAt, data.ends_at, null);
    var topShop = firstDefined(data.shopUrl, data.shop_url, data.url, null);

    var nr = null;
    if (reward && (reward.code || reward.label || reward.discountCode || reward.discount_code)) {
      nr = {
        label: firstDefined(reward.label, reward.title, reward.name, reward.rewardLabel, topLabel, topType, ""),
        code: firstDefined(reward.code, reward.promo, reward.promoCode, reward.discountCode, reward.discount_code, topCode, ""),
        expiresAt: firstDefined(reward.expiresAt, reward.expires_at, reward.expiry, topExpires, null) || null,
        shopUrl: firstDefined(reward.shopUrl, reward.shop_url, reward.url, topShop, null) || null
      };
    } else if (topCode || topLabel) {
      nr = { label: topLabel || topType || "Reward", code: topCode || "", expiresAt: topExpires || null, shopUrl: topShop || null };
    }

    var rank = firstDefined(data.rank, data.position, data.leaderboardPosition, data.leaderboard_position, (data.leaderboard && data.leaderboard.rank), null);
    var qualifies = firstDefined(data.qualifiesLeaderboard, data.qualifies_leaderboard, data.leaderboardQualified, data.qualifiesForLeaderboard, data.qualifies_for_leaderboard, (data.leaderboard && (data.leaderboard.qualified || data.leaderboard.qualifies)), false);
    var next = data.nextReward || data.next_reward || null, nn = null;
    if (next && (next.score || next.threshold)) nn = { score: Number(firstDefined(next.score, next.threshold, next.points, 0)) || 0, label: firstDefined(next.label, next.name, "rewards") };
    else nn = this._nextReward(displayScore);
    var isTop = firstDefined(data.isTopScorer, data.is_top_scorer, data.topScorer, (rank === 1), false);
    return { score: displayScore, rank: rank != null ? Number(rank) : null, qualifies: !!qualifies, isTopScorer: !!isTop, reward: nr, nextReward: nn, error: null };
  };

  Instance.prototype._fallbackResult = function (displayScore) {
    return { score: displayScore, rank: null, qualifies: false, isTopScorer: false, reward: null, nextReward: this._nextReward(displayScore), error: null };
  };

  /* ----------------------------- RESULT UI ------------------------------ */
  Instance.prototype._renderResult = function (res, err) {
    var L = this.cfg.labels, html = [];
    var scoreLine = tpl(L.scoredThisRound, { score: fmtNum(res.score) });
    var hasReward = !!(res.reward && (res.reward.code || res.reward.label));
    var qualifies = !!res.qualifies && !this._sessionClaimed();
    var isFirstPlace = !!(qualifies && (res.isTopScorer || Number(res.rank) === 1));
    var bodyClass = (hasReward && qualifies) ? "" : " wc26g-result__body--single";

    var title, icon, big = "";
    if (hasReward) {
      icon = "&#127942;";
      if (isFirstPlace) { title = L.firstPlaceTitle || "1st Place"; big = " wc26g-result__title--big"; }
      else { title = L.rewardTitle || "You did it"; }
    } else if (qualifies) { icon = "&#127941;"; title = L.leaderboardTitle || "New High Score"; }
    else { icon = "&#128546;"; title = L.noRewardTitle || "So close!"; }

    this.dom.result.className = "wc26g-card wc26g-result" + (hasReward ? " wc26g-result--reward" : "") + (qualifies ? " wc26g-result--leaderboard" : "") + (isFirstPlace ? " wc26g-result--first-place" : "");

    html.push('<div class="wc26g-result__header">');
    html.push('<div class="wc26g-result__icon">' + icon + "</div>");
    html.push('<div class="wc26g-result__title' + big + '">' + esc(title) + "</div>");
    if (qualifies && res.rank != null && !hasReward) html.push('<div class="wc26g-result__score">' + tpl(L.rankLine, { score: "<b>" + fmtNum(res.score) + "</b>", rank: res.rank }) + "</div>");
    else html.push('<div class="wc26g-result__score">' + scoreLine.replace(fmtNum(res.score), "<b>" + fmtNum(res.score) + "</b>") + "</div>");
    html.push('</div>');

    html.push('<div class="wc26g-result__body' + bodyClass + '">');

    if (qualifies) {
      html.push('<div class="wc26g-result__panel wc26g-result__panel--leaderboard">');
      html.push('<div class="wc26g-result__section-title">' + esc(L.topPrizeTitle || "") + "</div>");
      html.push('<div class="wc26g-result__muted">' + esc(L.topPrizeMsg || "") + "</div>");
      html.push('<div class="wc26g-form" data-wc26g-lbform>' +
        '<input class="wc26g-input" data-wc26g-name type="text" maxlength="24" placeholder="' + esc(L.namePlaceholder) + '" autocomplete="off">' +
        '<input class="wc26g-input" data-wc26g-phone type="tel" maxlength="24" placeholder="' + esc(L.phonePlaceholder) + '" autocomplete="off">' +
        '<div class="wc26g-form__note">' + esc(L.termsNote || "") + "</div>" +
        '<div class="wc26g-form__msg" data-wc26g-form-msg></div>' +
        '<button type="button" class="wc26g-btn wc26g-btn--light" data-wc26g-join>' + esc(L.joinLeaderboard || "Join the Leaderboard") + "</button></div>");
      html.push('</div>');
    }

    if (hasReward) {
      html.push('<div class="wc26g-result__panel wc26g-result__panel--reward">' + this._rewardBlockHtml(res.reward) + '</div>');
    } else if (qualifies) {
      html.push('<div class="wc26g-result__panel wc26g-result__panel--locked">');
      html.push('<div class="wc26g-locked">&#128274; ' + esc(L.lockedReward || "") + "</div>");
      if (res.nextReward) {
        html.push('<div class="wc26g-result__muted">' + tpl(L.reachToUnlock, { score: "<b>" + fmtNum(res.nextReward.score) + "</b>", label: esc(res.nextReward.label) }) + "</div>");
        html.push('<div class="wc26g-result__muted">' + esc(L.keepGoing || "") + "</div>");
      }
      html.push('</div>');
    } else {
      html.push('<div class="wc26g-result__panel wc26g-result__panel--locked">');
      if (res.nextReward) html.push('<div class="wc26g-result__muted">' + tpl(L.reachToUnlock, { score: "<b>" + fmtNum(res.nextReward.score) + "</b>", label: esc(res.nextReward.label || "rewards") }) + "</div>");
      html.push('<div class="wc26g-result__muted">' + esc(L.noRewardMsg || "") + "</div>");
      html.push('<div class="wc26g-locked">&#128274; ' + esc(L.lockedReward || "") + "</div>");
      html.push('</div>');
    }

    html.push('<div class="wc26g-result__actions">');
    if (hasReward) {
      html.push('<div class="wc26g-divider">OR</div>');
      if (res.nextReward) html.push('<div class="wc26g-result__muted">' + tpl(L.reachToUnlock, { score: "<b>" + fmtNum(res.nextReward.score) + "</b>", label: "rewards" }) + "<br>" + esc(L.keepGoing || "") + "</div>");
      else if (isFirstPlace) html.push('<div class="wc26g-result__muted wc26g-result__muted--first">' + esc(L.firstPlaceClosing || "You are the top scorer now. Keep your spot and stay ready for the final prize!") + "</div>");
      else html.push('<div class="wc26g-result__muted">' + esc(L.beatTopScorer || "") + "</div>");
    }
    html.push('<button type="button" class="wc26g-btn wc26g-btn--ghost" data-wc26g-tryagain>' + esc(L.tryAgain || "Try Again") + "</button>");
    html.push('<button type="button" class="wc26g-link" data-wc26g-back>' + esc(L.backToGames || "Back to games") + "</button>");
    html.push('</div>');
    html.push('</div>');

    this.dom.result.innerHTML = html.join("");
    this.dom.result.scrollTop = 0;
    this._hideAllOverlays();
    this._showOverlay("result");
    this._bindResultActions(res);
    if (isFirstPlace) this._runFirstPlaceConfetti();
    this._scrollGameIntoView(true);
  };

  Instance.prototype._runFirstPlaceConfetti = function () {
    if (typeof document === "undefined") return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    var existing = document.querySelector(".wc26g-confetti-canvas");
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

    var canvas = document.createElement("canvas");
    canvas.className = "wc26g-confetti-canvas wc26g-confetti-canvas--active";
    canvas.setAttribute("aria-hidden", "true");
    document.body.appendChild(canvas);

    var ctx = canvas.getContext && canvas.getContext("2d");
    if (!ctx) return;

    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = 0, h = 0;
    function resize() {
      w = Math.max(1, window.innerWidth || document.documentElement.clientWidth || 1);
      h = Math.max(1, window.innerHeight || document.documentElement.clientHeight || 1);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();

    var mobile = w < 640;
    var palette = ["#F1582B", "#FFB703", "#FFFFFF", "#FDE68A", "#22C55E", "#38BDF8", "#FB923C"];
    var pieces = [];
    var total = mobile ? 125 : 190;
    var duration = 4550;
    var emitWindow = 760;
    var start = performance.now();
    var raf = 0;

    function makePiece(i) {
      var lane = i % 3;
      var originX = lane === 0 ? w * 0.22 : (lane === 1 ? w * 0.5 : w * 0.78);
      originX += (Math.random() - 0.5) * (mobile ? 70 : 140);
      var originY = h + 34 + Math.random() * 54;
      var angle = (-Math.PI / 2) + (Math.random() - 0.5) * (mobile ? 0.95 : 1.25);
      var power = (mobile ? 13.0 : 16.0) + Math.random() * (mobile ? 8.5 : 10.5);
      var size = (mobile ? 5.5 : 6.5) + Math.random() * (mobile ? 7.5 : 9.5);
      var longRibbon = Math.random() < 0.36;
      return {
        x: originX,
        y: originY,
        vx: Math.cos(angle) * power + (Math.random() - 0.5) * 3.2,
        vy: Math.sin(angle) * power - Math.random() * 4.0,
        g: (mobile ? 0.42 : 0.48) + Math.random() * 0.10,
        drag: 0.988 + Math.random() * 0.006,
        sway: (Math.random() - 0.5) * (mobile ? 1.7 : 2.4),
        swaySpeed: 0.024 + Math.random() * 0.03,
        phase: Math.random() * Math.PI * 2,
        size: size,
        widthMul: longRibbon ? (2.2 + Math.random() * 1.2) : (0.75 + Math.random() * 0.6),
        heightMul: longRibbon ? (0.35 + Math.random() * 0.25) : (1.15 + Math.random() * 0.95),
        rotate: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * (0.18 + Math.random() * 0.20),
        color: palette[i % palette.length],
        shape: longRibbon ? "ribbon" : (Math.random() < 0.18 ? "circle" : "rect"),
        delay: Math.random() * emitWindow,
        life: duration - 320 + Math.random() * 420,
        alphaBoost: 0.78 + Math.random() * 0.22
      };
    }

    for (var i = 0; i < total; i++) pieces.push(makePiece(i));

    function drawPiece(p, elapsed) {
      var age = elapsed - p.delay;
      if (age <= 0 || age > p.life) return;

      var frame = age / 16.67;
      var t = age / 1000;
      var vx = p.vx * Math.pow(p.drag, frame);
      var vy = p.vy * Math.pow(p.drag, frame) + p.g * frame;
      var x = p.x + vx * frame * 0.78 + Math.sin(t * 7.5 + p.phase) * p.sway * frame;
      var y = p.y + p.vy * frame * 0.78 + 0.5 * p.g * frame * frame * 0.92;

      if (x < -90 || x > w + 90 || y > h + 100) return;

      var fadeIn = Math.min(1, age / 180);
      var fadeOut = age > p.life - 950 ? Math.max(0, (p.life - age) / 950) : 1;
      var alpha = fadeIn * fadeOut * p.alphaBoost;
      if (alpha <= 0.01) return;

      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(x, y);
      ctx.rotate(p.rotate + p.spin * frame + Math.sin(t * 6 + p.phase) * 0.45);
      ctx.fillStyle = p.color;

      if (p.shape === "circle") {
        ctx.beginPath();
        ctx.arc(0, 0, p.size * 0.48, 0, Math.PI * 2);
        ctx.fill();
      } else if (p.shape === "ribbon") {
        var rw = p.size * p.widthMul;
        var rh = p.size * p.heightMul;
        if (ctx.roundRect) {
          ctx.beginPath();
          ctx.roundRect(-rw / 2, -rh / 2, rw, rh, Math.max(2, rh * 0.45));
          ctx.fill();
        } else {
          ctx.fillRect(-rw / 2, -rh / 2, rw, rh);
        }
      } else {
        ctx.fillRect(-p.size * 0.45, -p.size * 0.95, p.size * 0.9, p.size * 1.9);
      }
      ctx.restore();
    }

    function drawGlow(elapsed) {
      if (elapsed > 920) return;
      var a = Math.max(0, 1 - elapsed / 920);
      ctx.save();
      var grad = ctx.createRadialGradient(w * 0.5, h + 10, 0, w * 0.5, h + 10, Math.min(w, h) * 0.78);
      grad.addColorStop(0, "rgba(241,88,43," + (0.22 * a) + ")");
      grad.addColorStop(0.45, "rgba(255,183,3," + (0.11 * a) + ")");
      grad.addColorStop(1, "rgba(241,88,43,0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    }

    function tick(now) {
      var elapsed = now - start;
      ctx.clearRect(0, 0, w, h);
      drawGlow(elapsed);
      for (var i = 0; i < pieces.length; i++) drawPiece(pieces[i], elapsed);
      if (elapsed < duration) raf = requestAnimationFrame(tick);
      else {
        ctx.clearRect(0, 0, w, h);
        canvas.classList.remove("wc26g-confetti-canvas--active");
        if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      }
    }

    raf = requestAnimationFrame(tick);
    this.timers.push(setTimeout(function () {
      cancelAnimationFrame(raf);
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
    }, duration + 900));
  };

  Instance.prototype._rewardBlockHtml = function (reward) {
    var L = this.cfg.labels, out = "";
    out += '<div class="wc26g-result__label">' + esc(L.yourReward || "Your Reward") + "</div>";
    out += '<div class="wc26g-result__reward">' + esc(reward.label || "Reward") + "</div>";
    if (reward.code) {
      out += '<div class="wc26g-result__label">' + esc(L.yourCode || "Your promo code") + "</div>";
      out += '<div class="wc26g-code"><span class="wc26g-code__value" data-wc26g-code>' + esc(reward.code) + '</span></div>';
      out += '<button type="button" class="wc26g-btn wc26g-btn--primary" data-wc26g-main-copy>' + esc(this.cfg.shopText || L.copyCode || "COPY CODE") + "</button>";
    }
    if (reward.expiresAt) {
      out += '<div class="wc26g-result__label" style="margin-top:12px">' + esc(L.expiresIn || "Expires in") + "</div>";
      out += '<div class="wc26g-timer" data-wc26g-timer></div>';
      out += '<div class="wc26g-form__note" style="text-align:center;margin-top:4px">' + esc(L.expiryNote || "") + "</div>";
    }
    return out;
  };

  Instance.prototype._bindResultActions = function (res) {
    var self = this, R = this.dom.result;
    var tryBtn = $(R, "[data-wc26g-tryagain]"); if (tryBtn) tryBtn.addEventListener("click", function () { self._startCountdown(); });
    var backBtn = $(R, "[data-wc26g-back]"); if (backBtn) backBtn.addEventListener("click", function () { self.goHome(); });
    var mainCopy = $(R, "[data-wc26g-main-copy]"); if (mainCopy) mainCopy.addEventListener("click", function () { self._copy(res.reward && res.reward.code, mainCopy); self._rememberReward(res.reward); });
    var copyBtn = $(R, "[data-wc26g-copy]"); if (copyBtn) copyBtn.addEventListener("click", function () { self._copy(res.reward && res.reward.code, copyBtn); self._rememberReward(res.reward); });
    var timerEl = $(R, "[data-wc26g-timer]"); if (timerEl && res.reward && res.reward.expiresAt) this._startExpiry(timerEl, res.reward.expiresAt, null);
    if (res.reward && res.reward.code && res.reward.expiresAt) this._rememberReward(res.reward);
    var joinBtn = $(R, "[data-wc26g-join]"); if (joinBtn) joinBtn.addEventListener("click", function () { self._submitPlayer(R); });
  };

  Instance.prototype._copy = function (text, btn) {
    if (!text) return;
    var L = this.cfg.labels, self = this;
    var done = function () { var orig = btn ? btn.innerHTML : (L.copyCode || "COPY CODE"); if (btn) btn.textContent = "\u2713 " + (L.copied || "Copied!"); self.timers.push(setTimeout(function () { if (btn) btn.innerHTML = orig; }, 2000)); };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done).catch(function () { self._copyFallback(text, done); });
    else this._copyFallback(text, done);
  };
  Instance.prototype._copyFallback = function (text, done) {
    try { var ta = document.createElement("textarea"); ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0"; document.body.appendChild(ta); ta.focus(); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); done(); } catch (e) {}
  };

  Instance.prototype._startExpiry = function (el, expiresAt, shopBtn) {
    var self = this, L = this.cfg.labels;
    var end = (typeof expiresAt === "number") ? expiresAt : Date.parse(expiresAt);
    if (isNaN(end)) { el.textContent = ""; return; }
    function units(h, m, s) {
      return '<div class="wc26g-timer__unit"><span class="wc26g-timer__num">' + pad2(h) + '</span><span class="wc26g-timer__lbl">Hour</span></div><span class="wc26g-timer__sep">:</span>' +
        '<div class="wc26g-timer__unit"><span class="wc26g-timer__num">' + pad2(m) + '</span><span class="wc26g-timer__lbl">Minutes</span></div><span class="wc26g-timer__sep">:</span>' +
        '<div class="wc26g-timer__unit"><span class="wc26g-timer__num">' + pad2(s) + '</span><span class="wc26g-timer__lbl">Seconds</span></div>';
    }
    function tick() {
      var rem = Math.floor((end - Date.now()) / 1000);
      if (rem <= 0) { el.innerHTML = '<div class="wc26g-result__muted" style="color:#ef9a9a">' + esc(L.codeExpired || "Code expired") + "</div>"; if (shopBtn) shopBtn.disabled = true; clearInterval(iv); return; }
      el.innerHTML = units(Math.floor(rem / 3600), Math.floor((rem % 3600) / 60), rem % 60);
    }
    tick(); var iv = setInterval(tick, 1000); this.timers.push(iv);
  };

  Instance.prototype._stickyKey = function () { return "wc26g_reward_" + (location.hostname || "site"); };
  Instance.prototype._rememberReward = function (reward) {
    if (!reward || !reward.code || !reward.expiresAt) return;
    var end = (typeof reward.expiresAt === "number") ? reward.expiresAt : Date.parse(reward.expiresAt);
    if (!end || isNaN(end) || end <= Date.now()) return;

    var data = {
      code: reward.code,
      label: reward.label || "Reward unlocked",
      expiresAt: end,
      shopUrl: reward.shopUrl || reward.shop_url || null
    };

    try { localStorage.setItem(this._stickyKey(), JSON.stringify(data)); } catch (e) {}

    try {
      window.dispatchEvent(new CustomEvent("wc26:reward-updated", { detail: data }));
    } catch (e) {}
  };

  Instance.prototype._restoreStickyReward = function () {
    try { window.dispatchEvent(new CustomEvent("wc26:reward-updated")); } catch (e) {}
  };

  Instance.prototype._showStickyReward = function () {
    /* The active promo code is now displayed globally by the Floating Search Tool Bar.
       Keep this method as a no-op so older calls do not create a second floating popup. */
  };

  /* --------------------------- player submit ---------------------------- */
  Instance.prototype._sessionClaimed = function () { return !!(this.session && this.claimedSessions[this.session.id]); };
  Instance.prototype._submitPlayer = function (R) {
    var self = this;
    var nameEl = $(R, "[data-wc26g-name]"), phoneEl = $(R, "[data-wc26g-phone]"), msg = $(R, "[data-wc26g-form-msg]"), joinBtn = $(R, "[data-wc26g-join]");
    var name = (nameEl.value || "").trim(), phone = (phoneEl.value || "").trim();
    var sessionId = this.session ? String(this.session.id || "") : "";
    var score = this.lastResult && this.lastResult.score != null ? Number(this.lastResult.score) : 0;

    msg.className = "wc26g-form__msg";
    msg.textContent = "";
    nameEl.classList.remove("wc26g-input--error");
    phoneEl.classList.remove("wc26g-input--error");

    if (name.length < 2) {
      nameEl.classList.add("wc26g-input--error");
      msg.textContent = "Please enter your name.";
      msg.className = "wc26g-form__msg wc26g-form__msg--err";
      return;
    }
    if (phone.replace(/\D/g, "").length < 6) {
      phoneEl.classList.add("wc26g-input--error");
      msg.textContent = "Please enter a valid phone number.";
      msg.className = "wc26g-form__msg wc26g-form__msg--err";
      return;
    }
    if (!sessionId) {
      msg.textContent = "Round session is missing. Please try again.";
      msg.className = "wc26g-form__msg wc26g-form__msg--err";
      return;
    }
    if (this._sessionClaimed()) {
      msg.textContent = "Already submitted for this round.";
      msg.className = "wc26g-form__msg wc26g-form__msg--err";
      return;
    }

    /* Send every common alias used by the admin/test-console versions of the
       backend. This keeps the storefront compatible whether the route expects
       camelCase, snake_case, player_name, username, or score/points. */
    var payload = {
      sessionId: sessionId,
      session_id: sessionId,
      gameSessionId: sessionId,
      game_session_id: sessionId,
      game: this.currentGame,
      gameKey: this.currentGame,
      game_key: this.currentGame,
      name: name,
      username: name,
      playerName: name,
      player_name: name,
      phone: phone,
      phoneNumber: phone,
      phone_number: phone,
      score: score,
      points: score,
      displayScore: score,
      rank: this.lastResult ? this.lastResult.rank : null,
      source: "shopify_theme_section",
      meta: {
        sectionId: this.id,
        submittedAt: new Date().toISOString()
      }
    };

    joinBtn.disabled = true;
    msg.textContent = "Submitting...";

    this.api.post(ENDPOINTS.playerSubmit, payload).then(function (data) {
      if (self.session) self.claimedSessions[self.session.id] = true;
      msg.textContent = (data && (data.message || data.successMessage)) || "You're on the leaderboard!";
      msg.className = "wc26g-form__msg wc26g-form__msg--ok";
      nameEl.disabled = true;
      phoneEl.disabled = true;
      joinBtn.textContent = "Submitted ✓";
      joinBtn.disabled = true;
      self.refreshLeaderboard(self.currentGame);
    }).catch(function (err) {
      joinBtn.disabled = false;
      if (window.console) console.warn("[wc26g] player submit failed", err && (err.data || err.message || err));
      var serverMsg = err && err.data && (err.data.message || err.data.error || err.data.details);
      msg.textContent = serverMsg || self.cfg.labels.errorGeneric || "Something went wrong. Please try again.";
      msg.className = "wc26g-form__msg wc26g-form__msg--err";
    });
  };

  /* ----------------------------- navigation ----------------------------- */
  Instance.prototype.goHome = function () { this._destroyEngine(); this._hideAllOverlays(); this.currentGame = null; this._showScreen("home"); };

  /* ------------------------------ best score ---------------------------- */
  Instance.prototype._bestKey = function (g) { return "wc26g:best:" + this.id + ":" + g; };
  Instance.prototype.getBest = function (g) { try { return parseInt(localStorage.getItem(this._bestKey(g)) || "0", 10) || 0; } catch (e) { return 0; } };
  Instance.prototype.setBest = function (g, score) { if (score > this.getBest(g)) { try { localStorage.setItem(this._bestKey(g), String(score)); } catch (e) {} } };

  /* ---------------------------- scroll lock ----------------------------- */
  Instance.prototype._lockScroll = function (on) { document.documentElement.classList.toggle("wc26g-noscroll", !!on); document.body.classList.toggle("wc26g-noscroll", !!on); };

  /* ----------------------------- visibility ----------------------------- */
  Instance.prototype.pause = function () { if (this.engine && this.engine.pause) this.engine.pause(); };
  Instance.prototype.resume = function () { if (this.engine && this.engine.resume) this.engine.resume(); };

  /* ------------------------------ teardown ------------------------------ */
  Instance.prototype.destroy = function () {
    this._destroyEngine();
    this.timers.forEach(function (t) { clearTimeout(t); clearInterval(t); }); this.timers = [];
    this.cleanups.forEach(function (fn) { try { fn(); } catch (e) {} }); this.cleanups = [];
    if (this._letsGoHandler && this.dom.letsGo) this.dom.letsGo.removeEventListener("click", this._letsGoHandler);
    this._lockScroll(false);
  };

  /* ======================================================================
     FLAPPY BALL ENGINE  (canvas, transparent over board background)
     ===================================================================== */
  function FlappyEngine(inst, cb) {
    this.inst = inst; this.cb = cb;
    this.cfg = inst.cfg.games.flappy_ball || {};
    this.canvas = inst.dom.canvas; this.ctx = this.canvas.getContext("2d");
    this.board = inst.dom.board; this.assets = inst.assets;
    this.raf = null; this.paused = false; this.listeners = [];
  }
  FlappyEngine.prototype.start = function () {
    var self = this, c = this.cfg;
    this.gravity = c.gravity || 0.2;
    this.flap = -(c.jump || 5.2);
    this.speed0 = c.obstacleSpeed || 1.8;
    this.maxDuration = 0; // games now end on loss, not on a forced timer
    this.orange = (getComputedStyle(this.inst.root).getPropertyValue("--wc26g-accent") || "#F1582B").trim() || "#F1582B";
    this.bgImg = loadImg(this.inst.boardBgUrl);

    this._resize();
    this._refreshPlaySizing();
    this.boundResize = function () { self._resize(); };
    window.addEventListener("resize", this.boundResize);

    this._buildPairs();

    this._refreshPlaySizing();
    this.ballX = Math.round(this.W * 0.22);
    this.ballY = this.H / 2;
    this.vy = 0; this.rot = 0;
    this.pipes = []; this.score = 0;
    this.state = "wait"; this.elapsed = 0; this.last = null;

    this._bindInput();
    if (this.inst.dom.startHint) this.inst.dom.startHint.textContent = c.instruction || "TAP ANYWHERE";
    this.inst._showOverlay("start");
    this.cb.onScore(0); this.cb.onTime(0);
    this._loop();
  };

  FlappyEngine.prototype._buildPairs = function () {
    var isD = this.inst.isDesktop();
    var src = this.inst.cfg.obstaclePairs || [];
    var pairs = src.map(function (p) { return { top: loadImg(isD ? (p.topD || p.topM) : (p.topM || p.topD)), bot: loadImg(isD ? (p.botD || p.botM) : (p.botM || p.botD)) }; });
    if (!pairs.length) {
      var legacy = this.inst.cfg.obstaclesLegacy || [];
      pairs = legacy.map(function (u) { var im = loadImg(u); return { top: im, bot: im }; });
    }
    this.pairs = pairs;
    this.pairN = 0;
  };
  FlappyEngine.prototype.onViewportChange = function () { this.bgImg = loadImg(this.inst.boardBgUrl); this._resize(); this._buildPairs(); };

  FlappyEngine.prototype._resize = function () {
    var rect = this.board.getBoundingClientRect();
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.W = rect.width || 320; this.H = rect.height || 480;
    this.canvas.width = Math.round(this.W * dpr); this.canvas.height = Math.round(this.H * dpr);
    this.canvas.style.width = this.W + "px"; this.canvas.style.height = this.H + "px";
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (this.ballX != null) this.ballX = Math.round(this.W * 0.22);
    this._refreshPlaySizing();
  };

  FlappyEngine.prototype._refreshPlaySizing = function () {
    var c = this.cfg || {};
    var isD = this.inst.isDesktop();
    this.pipeW = isD ? (c.pipeWidthDesktop || 90) : (c.pipeWidthMobile || 58);
    this.pipeW = clamp(this.pipeW, isD ? 52 : 42, isD ? 150 : 96);
    this.gap = isD ? (c.gapDesktop || 250) : (c.gapMobile || 165);
    this.gap = clamp(this.gap, isD ? 190 : 130, Math.max(isD ? 240 : 150, this.H - 120));
    this.spacing = isD ? (c.spacingDesktop || 460) : (c.spacingMobile || 250);
    this.spacing = clamp(this.spacing, isD ? 320 : 200, isD ? 820 : 420);
    this.assetScale = Number(c.assetScale || 0.8);
    this.assetScale = clamp(this.assetScale, 0.4, 1.2);
    this.ballR = Math.max(isD ? 18 : 11, Math.round(this.W * (isD ? 0.025 : 0.035)));
    if (this.ballR > 34) this.ballR = 34;
  };

  FlappyEngine.prototype._bindInput = function () {
    var self = this;
    var tap = function (e) { if (e && e.preventDefault) e.preventDefault(); self._onTap(); };
    this.board.addEventListener("pointerdown", tap);
    this.listeners.push(["pointerdown", tap]);
  };
  FlappyEngine.prototype.handleKey = function () { this._onTap(); };
  FlappyEngine.prototype._onTap = function () {
    if (this.state === "wait") { this.state = "running"; if (this.inst.dom.overlays.start) this.inst.dom.overlays.start.hidden = true; }
    if (this.state === "running") { this.vy = this.flap; this.rot -= 18; if (navigator.vibrate) try { navigator.vibrate(12); } catch (e) {} }
  };

  FlappyEngine.prototype._spawnPipe = function () {
    var marginTop = Math.max(36, this.H * 0.10);
    var marginBottom = Math.max(42, this.H * 0.12);
    var maxTop = this.H - marginBottom - this.gap;
    if (maxTop < marginTop) {
      this.gap = Math.max(120, this.H - marginTop - marginBottom);
      maxTop = this.H - marginBottom - this.gap;
    }
    var gapTop = marginTop + Math.random() * Math.max(10, (maxTop - marginTop));
    var pair = this.pairs.length ? this.pairs[this.pairN % this.pairs.length] : null;
    this.pairN++;
    this.pipes.push({ x: this.W + 8, gapTop: gapTop, gapBot: gapTop + this.gap, scored: false, pair: pair });
  };

  FlappyEngine.prototype._loop = function () { var self = this; this.raf = requestAnimationFrame(function (ts) { self._frame(ts); }); };
  FlappyEngine.prototype._frame = function (ts) {
    if (this.paused) { this.last = ts; this._loop(); return; }
    if (this.last == null) this.last = ts;
    var dt = (ts - this.last) / 16.667; if (dt > 3) dt = 3; if (dt < 0) dt = 0;
    var realDt = ts - this.last;
    this.last = ts;

    var ctx = this.ctx;
    ctx.clearRect(0, 0, this.W, this.H);
    this._drawBackground(ctx);

    if (this.state === "running") {
      this.elapsed += realDt / 1000;
      this.cb.onTime(this.elapsed);
      this.vy += this.gravity * dt; if (this.vy > 9) this.vy = 9;
      this.ballY += this.vy * dt;
      this.rot += this.vy * 2.2 * dt;

      if (!this.pipes.length || this.pipes[this.pipes.length - 1].x < this.W - this.spacing) this._spawnPipe();
      var speed = (this.speed0 + this.score * 0.025) * dt * (this.inst.isDesktop() ? 1.25 : 1);
      var self = this;
      for (var i = this.pipes.length - 1; i >= 0; i--) {
        var p = this.pipes[i]; p.x -= speed;
        if (!p.scored && p.x + this.pipeW < this.ballX) { p.scored = true; this.score++; this.cb.onScore(this.score); if (navigator.vibrate) try { navigator.vibrate(12); } catch (e) {} }
        if (p.x + this.pipeW < -2) this.pipes.splice(i, 1);
      }
      this.pipes.forEach(function (pp) { self._drawPipe(pp); });
      if (this._dead()) return this._die(false);
    } else if (this.state === "dead") {
      this.vy += this.gravity * 1.5 * dt; this.ballY += this.vy * dt; this.rot += 10 * dt;
      var s2 = this;
      this.pipes.forEach(function (pp) { pp.x -= 0.6 * dt; s2._drawPipe(pp); });
    } else {
      this._drawPipe({ x: this.W * 0.64, gapTop: this.H / 2 - this.gap / 2, gapBot: this.H / 2 + this.gap / 2, pair: this.pairs[0] || null });
    }
    this._drawBall();
    this._loop();
  };

  FlappyEngine.prototype._dead = function () {
    if (this.ballY - this.ballR <= 0 || this.ballY + this.ballR >= this.H) return true;
    for (var i = 0; i < this.pipes.length; i++) {
      var p = this.pipes[i];
      if (this.ballX + this.ballR > p.x && this.ballX - this.ballR < p.x + this.pipeW) {
        if (this.ballY - this.ballR < p.gapTop || this.ballY + this.ballR > p.gapBot) return true;
      }
    }
    return false;
  };

  FlappyEngine.prototype._die = function (timeUp) {
    if (this.state === "dead" || this.state === "over") return;
    var self = this; this.state = "dead";
    if (navigator.vibrate) try { navigator.vibrate([60, 30, 60]); } catch (e) {}
    this.inst.timers.push(setTimeout(function () { self.state = "over"; cancelAnimationFrame(self.raf); self.cb.onEnd(self.score, self.elapsed); }, timeUp ? 200 : 750));
  };

  FlappyEngine.prototype._drawBackground = function (ctx) {
    var bg = this.bgImg;
    if (!imgReady(bg)) return;
    var iw = bg.naturalWidth || bg.width, ih = bg.naturalHeight || bg.height;
    if (!iw || !ih) return;
    var scale = Math.max(this.W / iw, this.H / ih);
    var dw = iw * scale, dh = ih * scale;
    var dx = (this.W - dw) / 2, dy = (this.H - dh) / 2;
    try { ctx.drawImage(bg, dx, dy, dw, dh); } catch (e) {}
  };

  FlappyEngine.prototype._roundRectPath = function (ctx, x, y, w, h, r) {
    r = Math.max(0, Math.min(r || 0, Math.abs(w) / 2, Math.abs(h) / 2));
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  };
  FlappyEngine.prototype._drawRounded = function (x, y, w, h, r, fill, stroke, lineW) {
    if (h <= 0 || w <= 0) return;
    var ctx = this.ctx;
    ctx.save();
    this._roundRectPath(ctx, x, y, w, h, r);
    if (fill) { ctx.fillStyle = fill; ctx.fill(); }
    if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = lineW || 2; ctx.stroke(); }
    ctx.restore();
  };
  FlappyEngine.prototype._drawPipe = function (p) {
    var ctx = this.ctx, W = this.pipeW || 52, orange = this.orange;
    var r = Math.min(18, Math.max(8, W * 0.18));
    if (p.gapTop > 0) this._drawRounded(p.x, -r, W, p.gapTop + r, r, "rgba(10,12,18,0.92)", orange, 2);
    if (p.gapBot < this.H) this._drawRounded(p.x, p.gapBot, W, this.H - p.gapBot + r, r, "rgba(10,12,18,0.92)", orange, 2);
    if (p.pair) {
      var cw = Math.max(24, (W - 12) * this.assetScale);
      var ch = Math.min(cw * 1.25, Math.max(24, this.H * 0.22));
      var cx = p.x + (W - cw) / 2;
      if (p.pair.top && imgReady(p.pair.top) && p.gapTop > 28) this._drawCard(p.pair.top, cx, Math.max(8, p.gapTop - ch - 12), cw, Math.min(ch, p.gapTop - 16));
      if (p.pair.bot && imgReady(p.pair.bot) && (this.H - p.gapBot) > 28) this._drawCard(p.pair.bot, cx, p.gapBot + 12, cw, Math.min(ch, this.H - p.gapBot - 18));
    }
  };
  FlappyEngine.prototype._drawCard = function (img, x, y, w, h) {
    if (h <= 0) return;
    var ctx = this.ctx; ctx.save();
    var r = Math.min(14, Math.max(6, w * 0.16));
    this._roundRectPath(ctx, x, y, w, h, r);
    ctx.clip();
    try { ctx.drawImage(img, x, y, w, h); } catch (e) {}
    ctx.restore();
  };

  FlappyEngine.prototype._drawBall = function () {
    var ctx = this.ctx, r = this.ballR;
    ctx.save(); ctx.translate(this.ballX, this.ballY); ctx.rotate(this.rot * Math.PI / 180);
    if (imgReady(this.assets.ball)) ctx.drawImage(this.assets.ball, -r, -r, r * 2, r * 2);
    else { ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.fill(); ctx.strokeStyle = "#222"; ctx.lineWidth = 1.5; ctx.stroke(); }
    ctx.restore();
  };

  FlappyEngine.prototype.pause = function () { this.paused = true; };
  FlappyEngine.prototype.resume = function () { this.paused = false; this.last = null; };
  FlappyEngine.prototype.destroy = function () {
    cancelAnimationFrame(this.raf);
    var self = this; this.listeners.forEach(function (l) { self.board.removeEventListener(l[0], l[1]); });
    if (this.boundResize) window.removeEventListener("resize", this.boundResize);
    this.canvas.hidden = true;
    if (this.inst.dom.overlays.start) this.inst.dom.overlays.start.hidden = true;
  };

  /* ======================================================================
     GOALKEEPER ENGINE  (DOM). Loses when a ball reaches the line uncaught.
     ===================================================================== */
  function GoalkeeperEngine(inst, cb) {
    this.inst = inst; this.cb = cb;
    this.cfg = inst.cfg.games.goalkeeper || {};
    this.board = inst.dom.board; this.layer = inst.dom.gkBalls;
    this.keeper = inst.dom.gkKeeper; this.hint = inst.dom.gkHint;
    this.flash = inst.dom.flash; this.line = inst.dom.gkLine;
    this.assets = inst.assets;
    this.raf = null; this.paused = false; this.listeners = []; this.balls = [];
  }
  GoalkeeperEngine.prototype.start = function () {
    var c = this.cfg;
    this.maxDuration = c.maxDuration || 0;
    this.ballSpeed0 = c.ballSpeed || 2.4;
    this.spawnEvery = c.spawnInterval || 1700;
    this.score = 0; this.elapsed = 0; this.last = null;
    this.spawnAcc = 0; this.spawnDelay = this.spawnEvery;
    this.keeperFrac = 0.5; this.dragging = false; this.state = "running";

    var hasCatcherImage = !!(this.assets.gkCatcher && this.assets.gkCatcher.src);
    if (hasCatcherImage) {
      this.inst.dom.gkVisual.innerHTML = '<img src="' + this.assets.gkCatcher.src + '" alt="">';
      this.inst.dom.gkVisual.style.display = "";
      if (this.inst.dom.gkGoal) { this.inst.dom.gkGoal.textContent = ""; this.inst.dom.gkGoal.style.display = "none"; }
    } else {
      this.inst.dom.gkVisual.innerHTML = '<span class="wc26g-gk-keeper__glove">&#129508;</span>';
      this.inst.dom.gkVisual.style.display = "";
      if (this.inst.dom.gkGoal) { this.inst.dom.gkGoal.textContent = c.goalText || "Goal"; this.inst.dom.gkGoal.style.display = ""; }
    }
    if (this.hint) {
      this.hint.textContent = "\u2190 " + (c.instruction || "Slide Left & Right") + " \u2192";
      this.hint.style.display = "block";
      this.hint.classList.remove("is-hidden");
      var self = this;
      this.inst.timers.push(setTimeout(function () { if (self.hint) self.hint.classList.add("is-hidden"); }, 3000));
    }

    this._size();
    this._placeKeeper();
    this._bindInput();
    this.cb.onScore(0); this.cb.onTime(0);
    this._loop();
  };

  GoalkeeperEngine.prototype._size = function () {
    var rect = this.board.getBoundingClientRect();
    this.W = rect.width || 320; this.H = rect.height || 480;
    this.kw = this.keeper.offsetWidth || 80;
    this.kh = this.keeper.offsetHeight || 80;
    this.ballSize = clamp(Math.round(this.W * 0.09), 26, 46);

    /* The catcher bottom offset is now controlled from the Theme Editor.
       The catch line follows the same value so the visual position and gameplay stay synced. */
    var styles = window.getComputedStyle(this.inst.root);
    var bottomVar = this.inst.isDesktop() ? "--wc26g-catcher-bottom-d" : "--wc26g-catcher-bottom-m";
    var catcherBottom = parseFloat(styles.getPropertyValue(bottomVar));
    if (!isFinite(catcherBottom)) catcherBottom = this.inst.isDesktop() ? 54 : 56;

    this.lineY = this.H - catcherBottom - this.kh;
    if (this.line) this.line.style.top = this.lineY + "px";
  };
  GoalkeeperEngine.prototype.onViewportChange = function () { this._size(); this._placeKeeper(); };

  GoalkeeperEngine.prototype._placeKeeper = function () {
    var x = clamp(this.keeperFrac * this.W, this.kw / 2, this.W - this.kw / 2);
    this.keeperFrac = x / this.W;
    this.keeper.style.left = x + "px";
    this.keeper.style.transform = "translateX(-50%)";
  };

  GoalkeeperEngine.prototype._bindInput = function () {
    var self = this;
    var left = function () { return self.board.getBoundingClientRect().left; };
    var move = function (cx) { var x = clamp(cx - left(), self.kw / 2, self.W - self.kw / 2); self.keeperFrac = x / self.W; self.keeper.style.left = x + "px"; };
    var down = function (e) { self.dragging = true; if (self.hint) self.hint.classList.add("is-hidden"); move(e.touches ? e.touches[0].clientX : e.clientX); if (e.cancelable) e.preventDefault(); };
    var movef = function (e) { if (!self.dragging) { if (e.type === "mousemove") move(e.clientX); return; } move(e.touches ? e.touches[0].clientX : e.clientX); if (e.cancelable) e.preventDefault(); };
    var up = function () { self.dragging = false; };
    this.board.addEventListener("touchstart", down, { passive: false });
    this.board.addEventListener("touchmove", movef, { passive: false });
    this.board.addEventListener("touchend", up);
    this.board.addEventListener("mousedown", down);
    this.board.addEventListener("mousemove", movef);
    window.addEventListener("mouseup", up);
    this.listeners = [[this.board, "touchstart", down], [this.board, "touchmove", movef], [this.board, "touchend", up], [this.board, "mousedown", down], [this.board, "mousemove", movef], [window, "mouseup", up]];
  };

  GoalkeeperEngine.prototype._spawnBall = function () {
    var x = Math.random() * (this.W - this.ballSize);
    var el = document.createElement("div");
    el.className = "wc26g-gk-ball";
    el.style.width = this.ballSize + "px"; el.style.height = this.ballSize + "px";
    if (this.assets.gkBall && this.assets.gkBall.src) { var im = document.createElement("img"); im.src = this.assets.gkBall.src; im.alt = ""; im.onerror = function () { el.innerHTML = '<span class="wc26g-gk-ball__fb"></span>'; }; el.appendChild(im); }
    else el.innerHTML = '<span class="wc26g-gk-ball__fb"></span>';
    this.layer.appendChild(el);
    this.balls.push({ el: el, x: x, y: -this.ballSize, rot: 0, spin: Math.random() > 0.5 ? 1 : -1 });
  };

  GoalkeeperEngine.prototype._flashFx = function (kind) {
    if (!this.flash) return; this.flash.className = "wc26g-flash wc26g-flash--" + kind;
    var self = this; this.inst.timers.push(setTimeout(function () { self.flash.className = "wc26g-flash"; }, 360));
  };

  GoalkeeperEngine.prototype._loop = function () { var self = this; this.raf = requestAnimationFrame(function (ts) { self._frame(ts); }); };
  GoalkeeperEngine.prototype._frame = function (ts) {
    if (this.paused) { this.last = ts; this._loop(); return; }
    if (this.last == null) this.last = ts;
    var dt = (ts - this.last) / 16.667; if (dt > 3) dt = 3; if (dt < 0) dt = 0;
    var realDt = ts - this.last; this.last = ts;

    this.elapsed += realDt / 1000;
    this.cb.onTime(this.elapsed);
    /* No forced timeout: the game ends only when the player misses a ball. */
    var speed = (this.ballSpeed0 + this.elapsed * 0.04) * dt * (this.H / 480);
    this.spawnAcc += realDt;
    if (this.spawnAcc >= this.spawnDelay) { this.spawnAcc = 0; this.spawnDelay = Math.max(650, this.spawnDelay - 30); this._spawnBall(); }

    var keeperCx = (parseFloat(this.keeper.style.left) || this.W / 2);
    var ballR = this.ballSize / 2, lineY = this.lineY, self = this, lost = false;

    this.balls = this.balls.filter(function (b) {
      b.y += speed; b.rot += 3 * b.spin * dt;
      b.el.style.transform = "translate(" + b.x + "px," + b.y + "px) rotate(" + b.rot + "deg)";
      if (b.y + self.ballSize >= lineY) {
        var ballCx = b.x + ballR;
        if (Math.abs(ballCx - keeperCx) <= (self.kw / 2 + ballR * 0.7)) {
          b.el.remove(); self.score++; self.cb.onScore(self.score); self._flashFx("save");
          if (navigator.vibrate) try { navigator.vibrate(12); } catch (e) {}
          return false;
        }
        b.el.remove(); self._flashFx("miss"); lost = true; return false;
      }
      return true;
    });

    if (lost) return this._end();
    this._loop();
  };

  GoalkeeperEngine.prototype._end = function () {
    if (this.state === "over") return; this.state = "over";
    cancelAnimationFrame(this.raf); this._clearBalls();
    this.cb.onEnd(this.score, this.elapsed);
  };
  GoalkeeperEngine.prototype._clearBalls = function () { this.balls.forEach(function (b) { try { b.el.remove(); } catch (e) {} }); this.balls = []; };

  GoalkeeperEngine.prototype.pause = function () { this.paused = true; };
  GoalkeeperEngine.prototype.resume = function () { this.paused = false; this.last = null; };
  GoalkeeperEngine.prototype.handleKey = function () {};
  GoalkeeperEngine.prototype.destroy = function () {
    cancelAnimationFrame(this.raf); this._clearBalls();
    this.listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2]); }); this.listeners = [];
    if (this.inst.dom.gk) this.inst.dom.gk.hidden = true;
  };

  /* ======================================================================
     GLOBAL KEYBOARD (space / up) - only the actively-playing instance
     ===================================================================== */
  function bindKeyHandler() {
    if (keyHandlerBound) return; keyHandlerBound = true;
    document.addEventListener("keydown", function (e) {
      if (!activePlaying || !activePlaying.engine) return;
      if (e.code === "Space" || e.key === " " || e.code === "ArrowUp") { e.preventDefault(); if (activePlaying.engine.handleKey) activePlaying.engine.handleKey(); }
    }, { passive: false });
  }

  /* ======================================================================
     LIFECYCLE
     ===================================================================== */
  function initRoot(root) {
    if (!root || root.getAttribute("data-wc26g-init") === "1") return;
    root.setAttribute("data-wc26g-init", "1");
    try { var inst = new Instance(root); registry[inst.id] = inst; } catch (e) { if (window.console) console.error("[wc26g] init failed", e); }
  }
  function initAll(scope) { var nodes = (scope || document).querySelectorAll("[data-wc26-game-section]"); Array.prototype.forEach.call(nodes, initRoot); }
  function destroyRoot(root) { var id = root.getAttribute("data-section-id"); if (id && registry[id]) { registry[id].destroy(); delete registry[id]; } root.removeAttribute("data-wc26g-init"); }

  document.addEventListener("visibilitychange", function () { if (!activePlaying) return; if (document.hidden) activePlaying.pause(); else activePlaying.resume(); });

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", function () { initAll(); });
  else initAll();

  document.addEventListener("shopify:section:load", function (e) {
    var root = e.target.querySelector("[data-wc26-game-section]") || (e.target.matches && e.target.matches("[data-wc26-game-section]") ? e.target : null);
    if (root) { destroyRoot(root); initRoot(root); }
  });
  document.addEventListener("shopify:section:unload", function (e) {
    var root = e.target.querySelector("[data-wc26-game-section]") || (e.target.matches && e.target.matches("[data-wc26-game-section]") ? e.target : null);
    if (root) destroyRoot(root);
  });

  window.WC26Game = { registry: registry, ENDPOINTS: ENDPOINTS, initAll: initAll };
})();